<template>
  <div>
    <general-loader v-if="loading" loading/>
    <TabLayout v-else class="inventory-view" :tabs="tabs">
      <template v-slot:title>
        <artisio-breadcrumb :items="breadcrumbs"/>
      </template>
      <template v-slot:buttons>
        <b-button variant="primary" @click="onCreateClick" class="mr-2" v-permission="'change_inventoryitem'">
          <font-awesome-icon icon="plus-circle" class="icon"/>
          {{ $t('Create Inventory Item') }}
        </b-button>
        <b-dropdown v-permission="'change_inventoryitem'" right :text="$t('More')">
          <b-dropdown-item @click="onEditClick(inventory)">
            {{ $t('Edit Inventory') }}
          </b-dropdown-item>
          <b-dropdown-item v-permission="'change_invoice'" @click="onCreateManualInvoiceClick(inventory)">
            {{ $t('Create Manual Invoice') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onItemMoveClick">
            {{ $t('Relocate') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onQrCodeClick">
            {{ $t('View QR') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onAssignQRClick">
            {{ $t('Assign QR') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onWebappQrCodesGenerate"
                           v-if="inventoryQrCodeForWebappURL && hasArtisioTimedProvider">
            {{ $t('Generate Public QR for Webapp') }}
          </b-dropdown-item>
          <b-dropdown-item v-permission="'export_inventory'" @click="onInventoryStickerClick">
            {{ $t('Inventory Sticker') }}
          </b-dropdown-item>
        </b-dropdown>
      </template>
      <template v-slot:right-placeholder>
        <template v-if="routeFrom">
          <b-button :disabled="!previousUuid" @click="onPreviousClick" variant="outline-primary"
                    class="mb-0 mr-2 d-flex align-items-center">
            <font-awesome-icon icon="angle-double-left" class="icon mr-2"/>
            {{ $t('Previous Item') }}
          </b-button>
          <b-button :disabled="!nextUuid" @click="onNextClick" variant="outline-primary"
                    class="mb-0 d-flex align-items-center">
            {{ $t('Next Item') }}
            <font-awesome-icon icon="angle-double-right" class="icon ml-2"/>
          </b-button>
        </template>
      </template>
    </TabLayout>
    <inventory-modal/>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import InventoryModal from "../InventoryModal";
  import {MODEL_NAME_ITEM_LOCATION, SOURCE_INVENTORY_VIEW} from "../../constants";
  import ArtisioBreadcrumb from "../../../core/components/artisio-breadcrumb/ArtisioBreadcrumb";
  import TabLayout from "../../../core/components/layout/DefaultLayout/TabLayout";
  import InventoryService from "../InventoryService";
  import {
    ON_PDF_MODAL_DATA_RETRIEVE,
    ON_PDF_MODAL_OPEN,
    ON_QR_CODE_READER_MODAL_OPEN
  } from "@/plugins/event-bus/event-bus-names";
  import QrCodeService from "@/core/components/qr-code-generator/QrCodeService";
  import GeneralLoader from "@/core/components/general-loader/GeneralLoader";
  import EventBus from "@/plugins/event-bus";

  const {mapActions, mapState} = createNamespacedHelpers('inventory');
  const {mapActions: lotsMapActions} = createNamespacedHelpers('lots');
  const {mapActions: invoicesMapActions} = createNamespacedHelpers('invoices');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  /**
   * @author Saiat Kalbiev <kalbievich11@gmail.com>
   */
  export default {
    name: "InventoryView",
    components: {GeneralLoader, TabLayout, ArtisioBreadcrumb, InventoryModal},
    data: () => {
      return {
        routeFrom: null,
        previousUuid: null,
        previousPage: null,
        nextUuid: null,
        nextPage: null,
        breadcrumbs: [],
      };
    },
    computed: {
      ...mapState({
        loading: state => state.view.loading,
        inventory: state => state.view.currentInventory,
      }),
      ...globalMapState({
        enableContinuousAuction: state => state.enableContinuousAuction,
        inventoryQrCodeForWebappURL: state => state.inventoryQrCodeForWebappURL,
        hasArtisioTimedProvider: state => state.hasArtisioTimedProvider,
      }),
      tabs() {
        let tabs = [
          {title: this.$i18n.t('Dashboard'), icon: 'server', to: {name: 'inventory.dashboard'}},
          {
            title: this.enableContinuousAuction ? this.$i18n.t('Lots') : this.$i18n.t('Reservations'),
            icon: 'boxes',
            to: {name: 'inventory.reservations'}
          },
          {title: 'Terms', icon: 'percentage', to: {name: 'inventory.terms'}},
          {title: this.$i18n.t('Vendor Charges'), icon: 'percentage', to: {name: 'inventory.vendorCharges'}},
          {title: this.$i18n.t('Condition Reports'), icon: 'file-alt', to: {name: 'inventory.conditionReports'}},
          {title: this.$i18n.t('Condition Report Requests'), icon: 'file-alt', to: {name: 'inventory.conditionReportRequests'}},
          {title: this.$i18n.t('Attachments'), icon: 'copy', to: {name: 'inventory.attachments'}},
          {title: this.$i18n.t('Movement History'), icon: 'map-marker-alt', to: {name: 'inventory.movementHistory'}},
          {title: this.$i18n.t('Activities'), icon: 'stream', to: {name: 'inventory.activities'}},
        ]

        if (this.inventory && this.inventory.is_grouped_item) {
          tabs.splice(1, 0, {
            title: this.$i18n.t('Items'),
            icon: 'boxes',
            to: {name: 'inventory.sourceInventories'}
          });
        }

        return tabs;
      }
    },
    watch: {
      async '$route.params.id'() {
        await this.getInventory(this.$route.params.id);
        this.initBreadcrumbs();
        this.calculateInfo();
      },
      inventory() {
        this.initBreadcrumbs();
      }
    },
    methods: {
      ...mapActions([
        'getInventory',
        'showModal',
        'getTableParams',
        'updateRouteFrom',
        'updateInventoryTablePage',
        'updateInventoryItemLocation',
        'getModalObject'
      ]),
      ...lotsMapActions({
        getLotsTableData: 'getTableParams',
        updateLotsTablePage: 'updateLotsTablePage'
      }),
      ...invoicesMapActions({
        showManualInvoiceModal: 'showManualInvoiceModal'
      }),
      onEditClick(item) {
        this.showModal();
        this.getModalObject({uuid: item.uuid, source: SOURCE_INVENTORY_VIEW});
      },
      onCreateManualInvoiceClick(item) {
        if (item.available_quantity === 0) {
          this.$errorDialog(this.$i18n.t("Following item does not have sufficient quantity. Please, increase the quantity and try again."))
          return false;
        }

        this.showManualInvoiceModal({
          source: SOURCE_INVENTORY_VIEW,
          singleInventoryItem: item
        });
      },
      onCreateClick() {
        this.showModal({
          object: {
            receipt: this.inventory.receipt,
            receipt_uuid: this.inventory.receipt.uuid
          }
        });
      },
      async onQrCodeClick() {
        this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
          title: this.$options.filters.striptags(this.inventory.short_description),
        });
        let response = await QrCodeService.generateQrCode({
          model_type: 'inventoryitem',
          model_uuid: this.inventory.uuid,
        })
        if (response.success) {
          this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
            ...response.body,
          })
        }
      },
      onItemMoveClick() {
        this.$evBus.$emit(ON_QR_CODE_READER_MODAL_OPEN, {
          handler: async (result) => {
            let oldLocation = this.inventory.item_location.name;
            let response = await QrCodeService.getQrCodeInfo(result)
            if (response.success) {
              if (response.body.result.model_type === MODEL_NAME_ITEM_LOCATION) {
                const updateResponse = await this.updateInventoryItemLocation({
                  uuid: this.inventory.uuid,
                  item_location_uuid: response.body.result.model_uuid
                });
                if (updateResponse.success) {

                  let previousLocation = this.$createElement('b', {}, [`${this.$i18n.t('Previous location: ')}`])
                  let currentLocation = this.$createElement('b', {}, [`${this.$i18n.t('Current location: ')}`])
                  let br = this.$createElement('br')
                  let successDialogBody = this.$createElement('p', {class: "mb-0"}, [previousLocation, oldLocation, br, currentLocation, this.inventory.item_location.name]);

                  this.$successDialog(successDialogBody, this.$i18n.t('Item was successfully moved to new location'));

                  this.$toast('success', this.$i18n.t('Item was successfully moved to new location'));
                } else {
                  if (updateResponse.body.non_field_errors) {
                    this.$errorDialog(updateResponse.body.non_field_errors[0])
                  } else {
                    this.$toast('danger', this.$i18n.t('Something went wrong. Please try again.'))
                  }
                }
              } else {
                this.$errorDialog(this.$i18n.t('Please scan item location QR code'));
              }
            } else {
              if (response.body.non_field_errors) {
                this.$errorDialog(response.body.non_field_errors[0]);
              } else if (response.toastError) {
                this.$errorDialog(response.toastError)
              }
            }
          }
        });
      },
      initBreadcrumbs() {
        this.breadcrumbs = [
          {
            text: this.$i18n.t('Inventory'),
            to: {name: 'inventory'},
            icon: 'boxes'
          },
          {
            text: `${this.$options.filters.truncate(this.inventory.short_description, 50)}`
          }
        ];
      },
      onPreviousClick() {
        if (this.routeFrom.name === 'inventory' || this.routeFrom.name === 'receipt.inventory') {
          this.updateInventoryTablePage(this.previousPage)
        } else if (this.routeFrom.name === 'sales.lots') {
          this.updateLotsTablePage(this.previousPage)
        }

        this.$router.push({
          name: this.$route.name,
          params: {id: this.previousUuid},
        });
      },
      async onNextClick() {
        if (this.routeFrom.name === 'inventory' || this.routeFrom.name === 'receipt.inventory') {
          this.updateInventoryTablePage(this.nextPage)
        } else if (this.routeFrom.name === 'sales.lots') {
          this.updateLotsTablePage(this.nextPage)
        }

        this.$router.push({
          name: this.$route.name,
          params: {id: this.nextUuid},
        });
      },
      async calculateInfo() {
        if (this.routeFrom && ['inventory', 'receipt.inventory', 'sales.lots'].includes(this.routeFrom.name)) {
          this.updateRouteFrom(this.routeFrom.name);
          let searchParams;

          if (this.routeFrom.name === 'inventory' || this.routeFrom.name === 'receipt.inventory') {
            searchParams = await this.getTableParams();
          } else if (this.routeFrom.name === 'sales.lots') {
            searchParams = await this.getLotsTableData();
          }

          let {success, body} = await InventoryService.getInfo({inventory_uuid: this.inventory.uuid, ...searchParams});
          if (success) {
            this.previousUuid = body.previous;
            this.previousPage = body.previous_page;
            this.nextUuid = body.next;
            this.nextPage = body.next_page;
          }
        }
      },
      onAssignQRClick() {
        this.$evBus.$emit(ON_QR_CODE_READER_MODAL_OPEN, {
          handler: async (result) => {
            if (result) {
              await this.$confirm(
                  this.$i18n.t("Are you sure you want to assign the scanned QR code?"),
                  this.$i18n.t('QR code was successfully assigned'),
                  this.assignQRCode,
                  result
              );
            }
          }
        });
      },
      async assignQRCode(result) {
        let data = {
          model_type: "inventoryitem",
          model_uuid: this.inventory.uuid,
          qr_code_data: result
        }
        return InventoryService.assignQRToInventory(data);
      },
      async onWebappQrCodesGenerate() {
        let response = await InventoryService.getQrCodeForWebapp({uuids: [this.inventory.uuid]});

        if (response.success) {
          this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
            title: this.$i18n.t('Inventory QR Codes for Webapp'),
          });
          this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
            filename: this.$i18n.t('Inventory QR Codes for Webapp'),
            pdf_url: response.body.url
          })
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0], this.$i18n.t('Inventory QR Codes for Webapp Validation'));
          }
        }
      },
      async onInventoryStickerClick() {
        EventBus.$emit(ON_PDF_MODAL_OPEN, {title: this.$i18n.t('Inventory Sticker')});
        let response = await InventoryService.getInventorySticker(this.inventory.uuid);
        EventBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
          ...response.body,
          filename: `Inventory Sticker ${this.inventory.stock_no}`
        })
      },
    },
    async beforeMount() {
      const response = await this.getInventory(this.$route.params.id);
      if (!response.success) {
        await this.$errorDialog(this.$i18n.t('Inventory item not found.'));
        this.$generalGoBack(this)
      } else {
        this.initBreadcrumbs();
        this.$nextTick(async () => this.calculateInfo());
      }
    },
    beforeRouteEnter(to, from, next) {
      next(vm => {
        if (from && from.name) {
          vm.routeFrom = from;
        }
      })
    },
  }

</script>

<style lang="scss">
  @import "../scss/variables";

  .inventory-view {
    @include view-page-wrapper;
  }
</style>
